// src/components/financial/FinancialDashboard.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase.config';
import FinancialSummaryCards from './FinancialSummaryCards';
import ExpenseBreakdown from './ExpenseBreakdown';
import TransactionHistory from './TransactionHistory';
import FinancialCharts from './FinancialCharts';
import FinancialReports from './FinancialReports';
import '../../pages/UserDashboard.css';
import './FinancialDashboard.css';

function FinancialDashboard({ userData, onBack }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [financialData, setFinancialData] = useState({
    currentBalance: 0,
    totalCollected: 0,
    totalExpenses: 0,
    pendingDues: 0,
    reserveFund: 0
  });
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    fetchFinancialData();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [selectedMonth, selectedYear, darkMode]);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      // Fetch payments
      const { data: paymentData } = await supabase
        .from('payments')
        .select('*')
        .eq('payment_year', selectedYear)
        .eq('payment_month', selectedMonth)
        .eq('status', 'paid');

      // Fetch expenses
      const { data: expenseData } = await supabase
        .from('expenses')
        .select(`
          *,
          expense_categories (name, icon),
          service_vendors (name)
        `)
        .eq('fiscal_year', selectedYear)
        .eq('fiscal_month', selectedMonth)
        .in('status', ['approved', 'paid']);

      // Fetch all residents for pending calculation
      const { data: residents } = await supabase
        .from('residents')
        .select('id, flat_number')
        .eq('account_status', 'active')
        .neq('role', 'superadmin');

      // Calculate summaries
      const totalCollected = paymentData?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
      const totalExpenses = expenseData?.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0) || 0;
      
      // Calculate pending dues
      const uniqueFlats = [...new Set(residents?.map(r => r.flat_number) || [])];
      const paidFlats = [...new Set(paymentData?.map(p => p.flat_number) || [])];
      const pendingFlats = uniqueFlats.filter(flat => !paidFlats.includes(flat));
      const pendingDues = pendingFlats.length * 800;

      // Fetch last 6 months data for trends
      const sixMonthsData = await fetchSixMonthsData();

      setFinancialData({
        currentBalance: totalCollected - totalExpenses,
        totalCollected,
        totalExpenses,
        pendingDues,
        reserveFund: calculateReserveFund(sixMonthsData)
      });

      setPayments(paymentData || []);
      setExpenses(expenseData || []);
      setMonthlyData(sixMonthsData);

    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSixMonthsData = async () => {
    const monthsData = [];
    const currentDate = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      
      const { data: payments } = await supabase
        .from('payments')
        .select('amount')
        .eq('payment_year', year)
        .eq('payment_month', month)
        .eq('status', 'paid');
      
      const { data: expenses } = await supabase
        .from('expenses')
        .select('amount')
        .eq('fiscal_year', year)
        .eq('fiscal_month', month)
        .in('status', ['approved', 'paid']);
      
      const collected = payments?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0;
      const spent = expenses?.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0) || 0;
      
      monthsData.push({
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        year,
        collected,
        spent,
        balance: collected - spent
      });
    }
    
    return monthsData;
  };

  const calculateReserveFund = (monthlyData) => {
    return monthlyData.reduce((sum, month) => sum + month.balance, 0);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading financial data...</p>
      </div>
    );
  }

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className={`menu-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('overview');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📊</span>
            <span className="menu-label">Overview</span>
          </button>
          <button
            className={`menu-item ${activeTab === 'expenses' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('expenses');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">💸</span>
            <span className="menu-label">Expenses</span>
          </button>
          <button
            className={`menu-item ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('transactions');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📜</span>
            <span className="menu-label">Transactions</span>
          </button>
          <button
            className={`menu-item ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('reports');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📈</span>
            <span className="menu-label">Reports</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">💰 Financial Overview</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="financial-header desktop-only">
            <div className="header-left">
              {onBack && (
                <button onClick={onBack} className="back-button">
                  ← Back
                </button>
              )}
              <h2 className="page-title">💰 Financial Overview</h2>
            </div>
            
            <div className="header-controls">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="form-select"
              >
                {[...Array(12)].map((_, i) => (
                  <option key={i+1} value={i+1}>
                    {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
              
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="form-select"
              >
                {[2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Summary Cards */}
          <FinancialSummaryCards data={financialData} />

          {/* Tabs */}
          <div className="financial-tabs">
            {['overview', 'expenses', 'transactions', 'reports'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`tab-button ${activeTab === tab ? 'active' : ''}`}
              >
                {tab === 'overview' && '📊'} 
                {tab === 'expenses' && '💸'} 
                {tab === 'transactions' && '📜'} 
                {tab === 'reports' && '📈'} 
                <span className="tab-label">{tab}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="dashboard-card financial-content">
            {activeTab === 'overview' && (
              <FinancialCharts 
                monthlyData={monthlyData}
                expenses={expenses}
                payments={payments}
              />
            )}
            
            {activeTab === 'expenses' && (
              <ExpenseBreakdown 
                expenses={expenses}
                month={selectedMonth}
                year={selectedYear}
              />
            )}
            
            {activeTab === 'transactions' && (
              <TransactionHistory 
                expenses={expenses}
                payments={payments}
                month={selectedMonth}
                year={selectedYear}
              />
            )}
            
            {activeTab === 'reports' && (
              <FinancialReports
                financialData={financialData}
                expenses={expenses}
                payments={payments}
                month={selectedMonth}
                year={selectedYear}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default FinancialDashboard;